import React, { useEffect, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import client from '../lib/api';
import { useSettings } from '../context/SettingsContext';
import { renderSettingText } from '../lib/textTemplate';
import { brandName } from '../lib/brand';
import { CONTENT_PAGE_SLUG_ATTRIBUTE, HOOKS, hook } from '../lib/domHooks';
import PageContent from '../components/PageContent';

const FooterPage = () => {
  const { slug } = useParams();
  const settings = useSettings();
  const siteName = brandName(settings);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    client.get(`/pages/${encodeURIComponent(slug)}`)
      .then(({ data }) => {
        if (active) setPage(data);
      })
      .catch((requestError) => {
        if (active) {
          setPage(null);
          setError(requestError.response?.data?.detail || 'Sayfa yüklenemedi');
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    if (!page?.title) return undefined;
    const previous = document.title;
    // Marka henüz bilinmiyorsa başlık " | " ile bitmesin.
    const title = renderSettingText(page.title, { site_name: siteName });
    document.title = siteName ? `${title} | ${siteName}` : title;
    return () => { document.title = previous; };
  }, [page?.title, siteName]);

  if (loading) {
    return (
      <main className="min-h-[55vh] grid place-items-center px-5 pt-36">
        <Loader2 size={28} className="animate-spin text-[#3cc487]" />
      </main>
    );
  }

  if (error || !page) {
    return (
      <main className="min-h-[55vh] grid place-items-center px-5 pt-36">
        <section className="pusu-panel w-full max-w-xl p-8 text-center">
          <AlertCircle size={32} className="mx-auto mb-3 text-[#3cc487]" />
          <h1 className="text-xl font-black mb-2">Sayfa bulunamadı</h1>
          <p className="text-sm text-slate-400 mb-5">{error}</p>
          <Link to="/" className="pusu-btn-primary inline-flex px-5 py-3">Ana sayfaya dön</Link>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-[55vh] px-5 sm:px-8 pt-36 pb-8">
      <article
        className="pusu-panel max-w-5xl mx-auto overflow-hidden"
        {...hook(HOOKS.CONTENT_PAGE)}
        {...{ [CONTENT_PAGE_SLUG_ATTRIBUTE]: page.slug || slug }}
      >
        <header className="border-b border-white/[0.06] px-6 py-7 md:px-10 md:py-9">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#3cc487] mb-2">
            {siteName}
          </div>
          <h1 className="text-2xl md:text-3xl font-black">
            {renderSettingText(page.title, settings)}
          </h1>
        </header>
        <div className="px-6 py-8 md:px-10 md:py-10 text-[15px] leading-8 text-slate-300">
          <PageContent content={renderSettingText(page.content, settings)} />
        </div>
      </article>
    </main>
  );
};

export default FooterPage;
